import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';

/** `/settings/general` (一般タブ、v0.2.5 S-11)。tenant_settings の key/value に保存する。
 *  設計: docs/design/v025-store-node-hierarchy.md「コンフィグ画面」節。 */
type TenantSettings = {
  company_name_display?: string;
  logo_url?: string;
  fiscal_year_start_month?: number;
  /** v0.4 追加要件⑭ (D-464): 売上目標の未達アラートのしきい値 (0 < ratio <= 1、既定 0.9)。
   *  ⚠️ ノード別上書きは持たない (tenant-lite に inheritable_settings が存在しないため)。 */
  kpi_target_alert_ratio?: number;
};

export function GeneralSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<TenantSettings>({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<TenantSettings>('/tenant-settings'),
  });

  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [fiscalMonth, setFiscalMonth] = useState(4);
  const [alertPercent, setAlertPercent] = useState(90);

  useEffect(() => {
    if (!data) return;
    setCompanyName(data.company_name_display ?? '');
    setLogoUrl(data.logo_url ?? '');
    setFiscalMonth(data.fiscal_year_start_month ?? 4);
    setAlertPercent(Math.round((data.kpi_target_alert_ratio ?? 0.9) * 100));
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (v: { key: string; value: unknown }) =>
      apiFetch(`/tenant-settings/${v.key}`, { method: 'PUT', body: JSON.stringify({ value: v.value }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-settings'] });
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  if (isLoading) return <div className="text-neutral-sub">読み込み中…</div>;

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-h2">一般</h2>
        <p className="text-sm text-neutral-sub mt-1">表示名・ロゴ・会計年度の開始月・売上目標のアラート基準を設定します。</p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
        <div>
          <label className="block text-sm text-neutral-sub mb-1">会社名 (表示用)</label>
          {/* bugfix-01: 375px で入力欄＋ボタンが画面幅を超えないよう縦積み→横並びのレスポンシブ配置 */}
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={companyName} onChange={(e) => setCompanyName(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 rounded border border-neutral-line focus:outline-none focus:shadow-focus-ring" />
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'company_name_display', value: companyName })}
              className="whitespace-nowrap px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50 w-full sm:w-auto">保存</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">ロゴ URL</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://..."
              className="flex-1 min-w-0 px-3 py-2 rounded border border-neutral-line focus:outline-none focus:shadow-focus-ring" />
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'logo_url', value: logoUrl })}
              className="whitespace-nowrap px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50 w-full sm:w-auto">保存</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">会計年度の開始月</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={fiscalMonth} onChange={(e) => setFiscalMonth(Number(e.target.value))}
              className="min-w-0 px-3 py-2 rounded border border-neutral-line focus:outline-none focus:shadow-focus-ring">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}月</option>)}
            </select>
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'fiscal_year_start_month', value: fiscalMonth })}
              className="whitespace-nowrap px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50 w-full sm:w-auto">保存</button>
          </div>
          <p className="text-xs text-neutral-sub mt-1">売上目標の年度・四半期の区切りに使われます。</p>
        </div>

        {/* v0.4 追加要件⑭ (D-464) */}
        <div>
          <label className="block text-sm text-neutral-sub mb-1" htmlFor="kt-alert">売上目標の未達アラート基準</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <input
                id="kt-alert" type="number" inputMode="numeric" min={1} max={100}
                value={alertPercent}
                onChange={(e) => setAlertPercent(Number(e.target.value))}
                className="w-24 min-h-[44px] px-3 py-2 rounded border border-neutral-line focus:outline-none focus:shadow-focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="text-sm text-neutral-sub whitespace-nowrap">% 未満で通知</span>
            </div>
            <button type="button"
              disabled={saveMut.isPending || !(alertPercent > 0 && alertPercent <= 100)}
              onClick={() => saveMut.mutate({ key: 'kpi_target_alert_ratio', value: alertPercent / 100 })}
              className="whitespace-nowrap min-h-[44px] px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50 w-full sm:w-auto">保存</button>
          </div>
          <p className="text-xs text-neutral-sub mt-1">
            月が終わり、その月の実績データが揃った時点で判定します。データ未入力の月や未公開の月では通知しません。
          </p>
        </div>
      </div>
    </div>
  );
}
